import { Circle } from "react-leaflet";
import type { AlertRule, Settings } from "../api/client";

/** Where a rule's zone is centred right now, mirroring the engine's RuleContext.anchor. */
export function ruleAnchor(rule: AlertRule, settings: Settings | null | undefined): [number, number] | null {
  if (rule.anchor_mode === "fixed") {
    return rule.center_lat !== null && rule.center_lon !== null ? [rule.center_lat, rule.center_lon] : null;
  }
  if (!settings) return null;
  const live: [number, number] | null =
    settings.live_is_fresh && settings.live_lat !== null && settings.live_lon !== null
      ? [settings.live_lat, settings.live_lon]
      : null;
  const home: [number, number] | null =
    settings.home_lat !== null && settings.home_lon !== null ? [settings.home_lat, settings.home_lon] : null;
  if (rule.anchor_mode === "live") return live;
  if (rule.anchor_mode === "home") return home;
  return live ?? home;
}

/**
 * Every enabled alert zone, drawn faintly on the radar.
 *
 * A rule is a circle around an anchor, and until now that circle existed only as a number in a
 * form. Seeing it makes "why did that alert?" and "why didn't that one?" answerable at a glance —
 * the aircraft either crossed the ring or it didn't. Sleeping rules (night, quiet hours) are drawn
 * dimmer, so a quiet evening doesn't look like a broken one.
 */
export default function ZoneLayer({ rules, settings }: { rules: AlertRule[]; settings: Settings | null | undefined }) {
  return (
    <>
      {rules
        .filter((r) => r.enabled && !r.dormant_reason)
        .map((rule) => {
          const anchor = ruleAnchor(rule, settings);
          if (!anchor) return null;
          const asleep = Boolean(rule.sleep_reason);
          return (
            <Circle
              key={rule.id}
              center={anchor}
              radius={rule.radius_m}
              interactive={false}
              pathOptions={{
                color: "#4da3ff",
                weight: 1.5,
                opacity: asleep ? 0.25 : 0.6,
                dashArray: "4 6",
                fillColor: "#4da3ff",
                fillOpacity: asleep ? 0.02 : 0.06,
              }}
            />
          );
        })}
    </>
  );
}
